import { useEffect, useState } from 'react';

import type { GenRequest } from '@ai-studio/shared';

import { audioFileOf, isPixelRequest, previewKind, videoFileOf } from '../../../lib/generation.js';
import { pixelThumb, rawUrl } from '../../../lib/thumbnails.js';
import { LoopPlayer } from './LoopPlayer.js';
import { ModelOrbit } from './ModelOrbit.js';
import { PreviewFrame } from './PreviewFrame.js';
import { mediaUrl, VideoPreview } from './VideoPreview.js';
import { ZoomableImage } from './ZoomableImage.js';

/** The preview of a finished result: orbit viewer, zoomable picture or looping player. */
export function GenerationPreview({ request, projectId }: { request: GenRequest; projectId: number }) {
  const kind = previewKind(request);
  const version = request.updatedAt;
  const [pixel, setPixel] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    if (kind === 'pixel') void pixelThumb(projectId, request.out, version).then((u) => live && setPixel(u));
    return () => {
      live = false;
    };
  }, [kind, projectId, request.out, version]);

  if (kind === 'audio') return <LoopPlayer src={rawUrl(projectId, audioFileOf(request), version)} label={request.title} />;
  if (kind === 'video')
    return (
      <PreviewFrame>
        <VideoPreview
          src={mediaUrl(projectId, videoFileOf(request), version)}
          {...(request.preview && /\.png$/i.test(request.preview) ? { poster: rawUrl(projectId, request.preview, version) } : {})}
          label={request.title}
        />
      </PreviewFrame>
    );
  if (kind === 'image')
    return (
      <PreviewFrame>
        <ZoomableImage src={rawUrl(projectId, request.preview!, version)} alt={request.title} pixelated={isPixelRequest(request)} />
      </PreviewFrame>
    );
  if (kind === 'pixel')
    return pixel ? (
      <PreviewFrame>
        <ZoomableImage src={pixel} alt={request.title} pixelated />
      </PreviewFrame>
    ) : null;
  if (kind === 'model')
    return (
      <PreviewFrame>
        <ModelOrbit projectId={projectId} path={request.out} version={version} alt={request.title} />
      </PreviewFrame>
    );
  return null;
}
